import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, CheckCircle, Heart, Edit2, Trash2, Share2, Check, Flag } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import CommentSection from './CommentSection';
import FlagModal from './FlagModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FRONTEND_URL = process.env.REACT_APP_BACKEND_URL.replace('/api', ''); // Get frontend URL

const PostCard = ({ post, onUpdate, onDelete }) => {
  const navigate = useNavigate();
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(post.title);
  const [editBody, setEditBody] = useState(post.body);
  const [liked, setLiked] = useState(post.user_has_liked);
  const [likeCount, setLikeCount] = useState(post.like_count);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showFlagModal, setShowFlagModal] = useState(false);
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();

  const getTypeColor = (type) => {
    const colors = {
      Experience: 'bg-blue-100 text-blue-700',
      Question: 'bg-purple-100 text-purple-700',
      Advice: 'bg-green-100 text-green-700',
      Reflection: 'bg-pink-100 text-pink-700',
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  const handleLike = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast({
        title: 'Login required',
        description: 'Please sign in to like posts',
        variant: 'destructive',
      });
      return;
    }

    try {
      if (liked) {
        await axios.delete(`${API}/posts/${post.id}/like`);
        setLiked(false);
        setLikeCount(likeCount - 1);
      } else {
        await axios.post(`${API}/posts/${post.id}/like`);
        setLiked(true);
        setLikeCount(likeCount + 1);
      }
    } catch (error) {
      toast({
        title: 'Action failed',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    }
  };

  const toggleComments = async (e) => {
    e?.preventDefault();
    if (!showComments && !commentsLoaded) {
      try {
        const response = await axios.get(`${API}/posts/${post.id}/comments`);
        setComments(response.data);
        setCommentsLoaded(true);
      } catch (error) {
        toast({
          title: 'Failed to load comments',
          description: 'Please try again',
          variant: 'destructive',
        });
      }
    }
    setShowComments(!showComments);
  };

  const handleViewFullPost = (e) => {
    e?.preventDefault();
    navigate(`/myiniraa/post/${post.id}`);
  };

  const handleShare = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent card click
    const shareUrl = `${window.location.origin}/myiniraa/post/${post.id}`;
    
    try {
      // Try to use native share API first (mobile)
      if (navigator.share) {
        await navigator.share({
          title: post.title,
          text: `Check out this post: ${post.title}`,
          url: shareUrl,
        });
      } else {
        // Fallback to clipboard
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        toast({
          title: '✓ Link copied!',
          description: 'Post link copied to clipboard',
        });
        
        // Reset after 2 seconds
        setTimeout(() => {
          setCopied(false);
        }, 2000);
      }
    } catch (error) {
      // If clipboard fails or user cancels, ignore
      if (error.name !== 'AbortError') {
        
        toast({
          title: 'Could not copy link',
          description: 'Please try again',
          variant: 'destructive',
        });
      }
    }
  };


  const handleEdit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.patch(`${API}/posts/${post.id}`, {
        title: editTitle,
        body: editBody,
      });
      toast({
        title: 'Post updated!',
        description: 'Your changes have been saved.',
      });
      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      toast({
        title: 'Update failed',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      await axios.delete(`${API}/posts/${post.id}`);
      toast({
        title: 'Post deleted',
        description: 'The post has been removed.',
      });
      if (onDelete) onDelete(post.id);
    } catch (error) {
      toast({
        title: 'Delete failed',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    }
  };

  const handleCommentAdded = (newComment) => {
    setComments([...comments, newComment]);
  };


  const handleCardClick = (e) => {
    // Don't navigate if clicking on interactive elements
    if (
      e.target.closest('button') || 
      e.target.closest('a') ||
      e.target.closest('input') ||
      e.target.closest('textarea')
    ) {
      return;
    }
    navigate(`/myiniraa/post/${post.id}`);
  };

  const canEdit = user && (user.id === post.author_id || user.is_admin);

  return (
    <div 
      className="bg-white rounded-lg shadow-md hover:shadow-lg transition p-6 border-l-4 border-[#FF8C00] overflow-hidden cursor-pointer" 
      data-testid="post-card"
      onClick={handleCardClick}
    >
      {isEditing ? (
        <form onSubmit={handleEdit} className="space-y-4">
          <input
            type="text"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF8C00] outline-none"
            maxLength={300}
          />
          <textarea
            value={editBody}
            onChange={(e) => setEditBody(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF8C00] outline-none resize-none"
            rows={4}
          />
          <div className="flex space-x-2">
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-[#FF8C00] text-white rounded-lg hover:bg-[#E67E00] disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <>
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${getTypeColor(post.post_type)}`}>
                  {post.post_type}
                </span>
                {post.verified && (
                  <span className="flex items-center space-x-1 text-xs text-green-600" data-testid="verified-badge">
                    <CheckCircle className="w-3 h-3" />
                    <span>Verified by Iniraa</span>
                  </span>
                )}
                {post.updated_at && (
                  <span className="text-xs text-gray-400">(edited)</span>
                )}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2 break-words" data-testid="post-title">
                {post.title}
              </h3>
            </div>
            {canEdit && (
              <div className="flex space-x-2 ml-4">
                <button
                  onClick={(e) => { e.preventDefault(); setIsEditing(true); }}
                  className="text-gray-500 hover:text-[#FF8C00] transition"
                  data-testid="edit-post-button"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={handleDelete}
                  className="text-gray-500 hover:text-red-600 transition"
                  data-testid="delete-post-button"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}
            {/* Flag/Report button - Show for all authenticated users, including admins on any post */}
            {isAuthenticated && (
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowFlagModal(true); }}
                className="text-gray-400 hover:text-red-600 transition ml-4"
                title="Report this post"
                data-testid="flag-post-button"
              >
                <Flag className="w-4 h-4" />
              </button>
            )}
          </div>

          <p className="text-gray-600 mb-4 line-clamp-3 break-words overflow-hidden" data-testid="post-body">
            {post.body}
          </p>

          {/* Images Grid */}
          {post.images && post.images.length > 0 && (
            <div className={`mb-4 grid gap-2 ${
              post.images.length === 1 ? 'grid-cols-1' :
              post.images.length === 2 ? 'grid-cols-2' :
              'grid-cols-2'
            }`}>
              {post.images.slice(0, 4).map((imageUrl, index) => (
                <div
                  key={imageUrl}
                  className="relative overflow-hidden rounded-lg cursor-pointer hover:opacity-90 transition"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/myiniraa/post/${post.id}`);
                  }}
                >
                  <img
                    src={`${BACKEND_URL}${imageUrl}`}
                    alt={`Post image ${index + 1}`}
                    className="w-full h-32 object-cover"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between text-sm mb-3">
            <div className="flex items-center space-x-4 min-w-0 flex-shrink">
              <span className="text-gray-500 break-words" data-testid="post-author">
                By {post.author_screenname}
              </span>
              <span className="text-gray-400 whitespace-nowrap" data-testid="post-date">
                {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
              </span>
            </div>
            <div className="flex items-center space-x-4 flex-shrink-0">
              <button
                onClick={handleLike}
                className={`flex items-center space-x-1 transition ${
                  liked ? 'text-red-600' : 'text-gray-500 hover:text-red-600'
                }`}
                data-testid="like-button"
              >
                <Heart className={`w-4 h-4 ${liked ? 'fill-current' : ''}`} />
                <span data-testid="like-count">{likeCount}</span>
              </button>
              <button
                onClick={toggleComments}
                className="flex items-center space-x-1 text-gray-500 hover:text-[#FF8C00] transition"
                data-testid="comment-toggle-button"
              >
                <MessageCircle className="w-4 h-4" />
                <span data-testid="comment-count">{comments.length || post.comment_count || 0}</span>
              </button>
              <button
                onClick={handleShare}
                className={`flex items-center space-x-1 transition ${
                  copied 
                    ? 'text-green-600' 
                    : 'text-gray-500 hover:text-[#7B3F9E]'
                }`}
                title={copied ? "Copied!" : "Share post"}
                data-testid="share-button"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    <span className="text-xs">Copied!</span>
                  </>
                ) : (
                  <Share2 className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-2 py-1 bg-[#FFF4E6] text-[#FF8C00] rounded"
                  data-testid="post-tag"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Inline Comments Section - Limited to 2 comments */}
          {showComments && (
            <div className="mt-4 pt-4 border-t border-gray-200" data-testid="inline-comments">
              <CommentSection
                postId={post.id}
                comments={comments.slice(0, 2)} // Show only first 2 comments
                onCommentAdded={handleCommentAdded}
                inline={true}
              />
              
              {/* "See More" CTA when there are more than 2 comments */}
              {comments.length > 2 && (
                <div className="mt-4 text-center">
                  <button
                    onClick={handleViewFullPost}
                    className="inline-flex items-center space-x-2 text-[#FF8C00] hover:text-[#E67E00] font-medium transition group"
                    data-testid="see-more-comments"
                  >
                    <MessageCircle className="w-4 h-4 group-hover:scale-110 transition-transform" />
                    <span>See more ({comments.length - 2} more {comments.length - 2 === 1 ? 'comment' : 'comments'})</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
      
      {/* Flag Modal */}
      <FlagModal
        isOpen={showFlagModal}
        onClose={() => setShowFlagModal(false)}
        contentType="post"
        contentId={post.id}
        contentPreview={post.title}
      />
    </div>
  );
};

export default PostCard;
